import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Site, MapSection, Reservation, ScheduleEvent, PendingQuickAction, QuickActionPayment, ModificationPreview } from '../../types';
import { MemberReview } from '../../types/memberReview';
import { SiteBlackout, SiteBlackoutUpdate } from '../../types/site';
import { getDayOfWeek, getDayNumber, isToday, getMonthName, getLocalDateString } from '../../utils/dateUtils';
import { getStatusColor } from '../../utils/formatters';
import { parseSpecialRequests } from '../../services/reservationService';
import ReservationQuickActionPopover from './ReservationQuickActionPopover';
import ReservationExtendChoicePopover from './ReservationExtendChoicePopover';
import BlackoutDetailPopover from './BlackoutDetailPopover';
import './ScheduleGrid.css';

interface ScheduleGridProps {
  sites: Site[];
  sections: MapSection[];
  dates: string[];
  reservations: Reservation[];
  onReservationClick: (reservation: Reservation) => void;
  highlightReservationId?: number;
  onScroll?: (scrollLeft: number, scrollTop: number) => void;
  blackouts?: SiteBlackout[];
  scheduleEvents?: ScheduleEvent[];
  reservationReviews?: Map<number, MemberReview>;
  onEmptyCellClick?: (site: Site, date: string) => void;
  onLoadQuickActionPreview?: (action: PendingQuickAction) => Promise<ModificationPreview>;
  onExecuteQuickAction?: (action: PendingQuickAction, reason: string, payment: QuickActionPayment) => Promise<void>;
  // Supplied together: blackout cells are only clickable when the grid's owner
  // can actually persist a change.
  onUpdateBlackout?: (blackoutId: number, update: SiteBlackoutUpdate) => Promise<void>;
  onDeleteBlackout?: (blackoutId: number) => Promise<void>;
}

const DRAG_THRESHOLD_PX = 5;
// Click-to-extend only offers to extend across a reasonably short gap — a multi-week empty
// stretch isn't "one more night," it's a new booking, so cells beyond this fall back to click-to-create.
const MAX_EXTEND_NIGHTS = 2;

type ExtendCandidate = { reservation: Reservation; edge: 'checkin' | 'checkout'; nights: number };

const buildExtendAction = (info: ExtendCandidate, date: string): PendingQuickAction => {
  const { reservation, edge, nights } = info;
  return edge === 'checkin'
    ? {
        type: 'extend-checkin',
        reservation,
        targetSiteAlias: reservation.siteAlias,
        targetCheckIn: date,
        targetDays: reservation.days + nights,
      }
    : {
        type: 'extend-checkout',
        reservation,
        targetSiteAlias: reservation.siteAlias,
        targetCheckIn: reservation.checkIn,
        targetDays: reservation.days + nights,
      };
};

const ScheduleGrid: React.FC<ScheduleGridProps> = ({
  sites,
  sections,
  dates,
  reservations,
  onReservationClick,
  highlightReservationId,
  onScroll: onScrollProp,
  blackouts,
  scheduleEvents,
  reservationReviews,
  onEmptyCellClick,
  onLoadQuickActionPreview,
  onExecuteQuickAction,
  onUpdateBlackout,
  onDeleteBlackout,
}) => {
  // Collapsible sections
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
      return next;
    });
  };

  // Resizable site column
  const MIN_SITE_COL = 60;
  const MAX_SITE_COL = 400;
  const [siteColumnWidth, setSiteColumnWidth] = useState(() =>
    window.innerWidth < 768 ? 80 : 220
  );
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current) return;
    const delta = e.clientX - dragStartX.current;
    const newWidth = Math.min(MAX_SITE_COL, Math.max(MIN_SITE_COL, dragStartWidth.current + delta));
    setSiteColumnWidth(newWidth);
  }, []);

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = siteColumnWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [siteColumnWidth, onMouseMove, onMouseUp]);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging.current) return;
    const delta = e.touches[0].clientX - dragStartX.current;
    const newWidth = Math.min(MAX_SITE_COL, Math.max(MIN_SITE_COL, dragStartWidth.current + delta));
    setSiteColumnWidth(newWidth);
  }, []);

  const onTouchEnd = useCallback(() => {
    isDragging.current = false;
    document.removeEventListener('touchmove', onTouchMove);
    document.removeEventListener('touchend', onTouchEnd);
  }, [onTouchMove]);

  const onResizeTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.touches[0].clientX;
    dragStartWidth.current = siteColumnWidth;
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  }, [siteColumnWidth, onTouchMove, onTouchEnd]);

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  // Dynamic grid columns based on number of dates
  const gridColumns = useMemo(() => {
    const colWidth = dates.length > 14 ? '45px' : '70px';
    return `${siteColumnWidth}px repeat(${dates.length}, ${colWidth})`;
  }, [dates.length, siteColumnWidth]);

  // Group sites by section
  const sitesBySection = useMemo(() => {
    const grouped: Record<string, Site[]> = {};
    sections.forEach(section => {
      const filtered = sites.filter(site => site.section === section.id);
      const sorted = filtered.sort((a, b) =>
        a.alias.localeCompare(b.alias, undefined, { numeric: true, sensitivity: 'base' })
      );

      grouped[section.id] = sorted;
    });
    return grouped;
  }, [sites, sections]);

  // Create a map of site -> date -> reservation for quick lookup
  const reservationMap = useMemo(() => {
    const map: Record<string, Record<string, Reservation>> = {};

    reservations.forEach(res => {
      if (!map[res.siteAlias]) {
        map[res.siteAlias] = {};
      }

      // Mark all dates the reservation spans
      let currentDate = res.checkIn;
      while (currentDate < res.checkOut) {
        map[res.siteAlias][currentDate] = res;
        // Move to next day
        const date = new Date(currentDate + 'T12:00:00');
        date.setDate(date.getDate() + 1);
        currentDate = getLocalDateString(date);
      }
    });

    return map;
  }, [reservations]);

  // Same lookup as reservationMap above, but keyed by site.id instead of site.alias.
  // The quick-action features (extend/drag) use THIS one — alias strings aren't guaranteed
  // unique across sites/sections, so building occupancy off site.id avoids two unrelated site
  // rows that happen to share an alias bleeding into each other's occupancy data.
  const reservationMapBySiteId = useMemo(() => {
    const map: Record<number, Record<string, Reservation>> = {};

    reservations.forEach(res => {
      if (!map[res.siteId]) {
        map[res.siteId] = {};
      }

      let currentDate = res.checkIn;
      while (currentDate < res.checkOut) {
        map[res.siteId][currentDate] = res;
        const date = new Date(currentDate + 'T12:00:00');
        date.setDate(date.getDate() + 1);
        currentDate = getLocalDateString(date);
      }
    });

    return map;
  }, [reservations]);

  // Build blackout lookup: site_id → date → SiteBlackout
  const blackoutMap = useMemo(() => {
    const map: Record<number, Record<string, SiteBlackout>> = {};
    blackouts?.forEach(b => {
      if (!b.active) return;
      if (!map[b.site_id]) map[b.site_id] = {};
      const start = new Date(b.start_date + 'T12:00:00');
      const end = new Date(b.end_date + 'T12:00:00');
      const current = new Date(start);
      while (current < end) {
        const dateStr = getLocalDateString(current);
        map[b.site_id][dateStr] = b;
        current.setDate(current.getDate() + 1);
      }
    });
    return map;
  }, [blackouts]);

  // Single-site gating: quick actions (extend/create/drag) only apply to reservations
  // that occupy exactly one site row — multi-site reservations keep today's click-to-popup behavior,
  // since the backend has no way to change one site's dates independently of its siblings.
  const reservationIdCounts = useMemo(() => {
    const counts = new Map<number, number>();
    reservations.forEach(r => counts.set(r.id, (counts.get(r.id) || 0) + 1));
    return counts;
  }, [reservations]);

  const isSingleSite = useCallback(
    (r: Reservation) => (reservationIdCounts.get(r.id) ?? 0) === 1,
    [reservationIdCounts]
  );

  // For each single-site reservation, find the contiguous run of empty cells immediately
  // before check-in and immediately after checkout — each such cell is a click-to-extend target.
  // Keyed by site.id (a unique DB key), NOT site.alias — alias strings are not guaranteed unique
  // across sections/sites, and keying by alias let two unrelated site rows that happen to share
  // an alias bleed into the same map bucket, attributing a cell to a reservation on a different row.
  //
  // A cell within range of exactly one reservation extends that reservation directly. A cell
  // within range of MORE THAN ONE reservation (their extend zones overlap) is ambiguous — every
  // candidate is kept, and the manager is asked which one to extend rather than the system
  // silently guessing.
  const adjacentExtendableMap = useMemo(() => {
    const map: Record<number, Record<string, ExtendCandidate[]>> = {};
    if (dates.length === 0) return map;

    reservations.forEach(res => {
      if (!isSingleSite(res)) return;

      const isEmptyCell = (date: string) =>
        !reservationMapBySiteId[res.siteId]?.[date] && !blackoutMap[res.siteId]?.[date];

      if (!map[res.siteId]) map[res.siteId] = {};

      const addCandidate = (date: string, edge: 'checkin' | 'checkout', nights: number) => {
        if (!map[res.siteId][date]) map[res.siteId][date] = [];
        map[res.siteId][date].push({ reservation: res, edge, nights });
      };

      // Walk backward from the night before check-in
      const checkInIdx = dates.indexOf(res.checkIn);
      if (checkInIdx > 0) {
        let nights = 0;
        for (let idx = checkInIdx - 1; idx >= 0 && nights < MAX_EXTEND_NIGHTS; idx--) {
          const date = dates[idx];
          if (!isEmptyCell(date)) break;
          nights += 1;
          addCandidate(date, 'checkin', nights);
        }
      }

      // Walk forward from checkout (the checkout date is already the first empty day, half-open range)
      const checkOutIdx = dates.indexOf(res.checkOut);
      if (checkOutIdx !== -1) {
        let nights = 0;
        for (let idx = checkOutIdx; idx < dates.length && nights < MAX_EXTEND_NIGHTS; idx++) {
          const date = dates[idx];
          if (!isEmptyCell(date)) break;
          nights += 1;
          addCandidate(date, 'checkout', nights);
        }
      }
    });

    return map;
  }, [reservations, dates, reservationMapBySiteId, blackoutMap, isSingleSite]);

  // Pending quick action (click-to-extend result or drag-and-drop drop result), shown in a confirm popover
  const [pendingAction, setPendingAction] = useState<PendingQuickAction | null>(null);
  const [pendingAnchor, setPendingAnchor] = useState<DOMRect | null>(null);

  const openPendingAction = useCallback((action: PendingQuickAction, anchor: DOMRect) => {
    setPendingAction(action);
    setPendingAnchor(anchor);
  }, []);

  const closePendingAction = useCallback(() => {
    setPendingAction(null);
    setPendingAnchor(null);
  }, []);

  // When a cell is only reachable from one reservation, extend it directly. When its extend
  // zone overlaps with another reservation's, let the manager pick which one rather than guessing.
  const [pendingChoice, setPendingChoice] = useState<{ candidates: ExtendCandidate[]; date: string; anchor: DOMRect } | null>(null);

  const closePendingChoice = useCallback(() => setPendingChoice(null), []);

  const handleExtendCellClick = useCallback(
    (e: React.MouseEvent, candidates: ExtendCandidate[], date: string) => {
      const anchor = e.currentTarget.getBoundingClientRect();
      if (candidates.length === 1) {
        openPendingAction(buildExtendAction(candidates[0], date), anchor);
      } else {
        setPendingChoice({ candidates, date, anchor });
      }
    },
    [openPendingAction]
  );

  const handleChooseCandidate = useCallback(
    (candidate: ExtendCandidate) => {
      if (!pendingChoice) return;
      openPendingAction(buildExtendAction(candidate, pendingChoice.date), pendingChoice.anchor);
      setPendingChoice(null);
    },
    [pendingChoice, openPendingAction]
  );

  // ---- Drag-and-drop: move a single-site reservation to another site and/or date range ----

  interface DragVisual {
    reservation: Reservation;
    ghostX: number;
    ghostY: number;
    targetSiteId: number;
    targetCheckInIdx: number;
    nights: number;
    valid: boolean;
  }
  const [dragVisual, setDragVisual] = useState<DragVisual | null>(null);
  const dragVisualRef = useRef<DragVisual | null>(null);
  useEffect(() => {
    dragVisualRef.current = dragVisual;
  }, [dragVisual]);

  const dragReservation = useRef<Reservation | null>(null);
  const resDragStartX = useRef(0);
  const resDragStartY = useRef(0);
  const dragOriginCheckInIdx = useRef(0);
  const dragMoved = useRef(false);
  const dragColWidth = useRef(70);

  // Blackout detail popover. Holds the blackout's ID rather than the record:
  // when a blackout is deactivated or deleted the owner drops it from
  // `blackouts`, the lookup below finds nothing, and the popover unmounts on its
  // own — no close-on-success bookkeeping.
  const [blackoutPopover, setBlackoutPopover] =
    useState<{ blackoutId: number; anchor: DOMRect } | null>(null);
  const closeBlackoutPopover = useCallback(() => setBlackoutPopover(null), []);

  const blackoutsEditable = !!onUpdateBlackout && !!onDeleteBlackout;

  const handleBlackoutCellClick = useCallback((e: React.MouseEvent, blackout: SiteBlackout) => {
    // A reservation drag that finishes over a blackout cell must not open the editor.
    if (dragReservation.current || dragMoved.current) return;
    setBlackoutPopover({ blackoutId: blackout.id, anchor: e.currentTarget.getBoundingClientRect() });
  }, []);

  const openBlackout = useMemo(
    () => (blackoutPopover ? blackouts?.find(b => b.id === blackoutPopover.blackoutId) : undefined),
    [blackoutPopover, blackouts]
  );

  // The record went away (deactivated, deleted, or refreshed out) — drop the
  // dangling ID so the backdrop doesn't linger.
  useEffect(() => {
    if (blackoutPopover && !openBlackout) setBlackoutPopover(null);
  }, [blackoutPopover, openBlackout]);

  // Stable event-listener handles (assigned below), so add/removeEventListener always target the same reference
  const onDragMouseMoveHandler = useRef((_e: MouseEvent) => {});
  const onDragMouseUpHandler = useRef(() => {});
  const onDragTouchMoveHandler = useRef((_e: TouchEvent) => {});

  // Resolve the target by site.id (unique), not alias — alias strings can repeat across
  // sites/sections, and matching by alias could silently resolve to the wrong physical site.
  const isDropTargetValid = useCallback(
    (reservation: Reservation, targetSiteId: number, targetCheckInIdx: number, nights: number): boolean => {
      if (targetCheckInIdx < 0) return false;
      const targetSite = sites.find(s => s.id === targetSiteId);
      if (!targetSite) return false;
      if (collapsedSections.has(targetSite.section)) return false;
      for (let idx = targetCheckInIdx; idx < targetCheckInIdx + nights; idx++) {
        const date = dates[idx];
        if (date == null) return false; // extends beyond the loaded date window
        const existing = reservationMapBySiteId[targetSite.id]?.[date];
        if (existing && existing.id !== reservation.id) return false;
        if (blackoutMap[targetSite.id]?.[date]) return false;
      }
      return true;
    },
    [sites, dates, collapsedSections, reservationMapBySiteId, blackoutMap]
  );
  const isDropTargetValidRef = useRef(isDropTargetValid);
  useEffect(() => {
    isDropTargetValidRef.current = isDropTargetValid;
  }, [isDropTargetValid]);

  const computeDragVisual = useCallback((reservation: Reservation, clientX: number, clientY: number): DragVisual => {
    const dx = clientX - resDragStartX.current;
    const deltaDays = Math.round(dx / dragColWidth.current);
    const targetCheckInIdx = dragOriginCheckInIdx.current + deltaDays;

    const el = document.elementFromPoint(clientX, clientY);
    const rowEl = el ? (el as HTMLElement).closest('[data-site-id]') as HTMLElement | null : null;
    const targetSiteIdAttr = rowEl?.getAttribute('data-site-id');
    const targetSiteId = targetSiteIdAttr != null ? Number(targetSiteIdAttr) : reservation.siteId;

    const nights = reservation.days;
    const valid = isDropTargetValidRef.current(reservation, targetSiteId, targetCheckInIdx, nights);

    return { reservation, ghostX: clientX, ghostY: clientY, targetSiteId, targetCheckInIdx, nights, valid };
  }, []);

  const finishDrag = useCallback(() => {
    document.removeEventListener('mousemove', onDragMouseMoveHandler.current);
    document.removeEventListener('mouseup', onDragMouseUpHandler.current);
    document.removeEventListener('touchmove', onDragTouchMoveHandler.current);
    document.removeEventListener('touchend', onDragMouseUpHandler.current);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    const reservation = dragReservation.current;
    const wasMoved = dragMoved.current;
    const visual = dragVisualRef.current;

    dragReservation.current = null;
    dragMoved.current = false;
    setDragVisual(null);

    if (!reservation) return;

    if (!wasMoved) {
      onReservationClick(reservation);
      return;
    }

    if (!visual || !visual.valid) return; // invalid target: snap back, no popover

    const targetCheckIn = dates[visual.targetCheckInIdx];
    if (!targetCheckIn) return;

    const targetSite = sites.find(s => s.id === visual.targetSiteId);
    if (!targetSite) return;

    const siteChanged = targetSite.id !== reservation.siteId;
    const datesChanged = targetCheckIn !== reservation.checkIn;
    if (!siteChanged && !datesChanged) return; // dropped back where it started

    let type: PendingQuickAction['type'];
    if (siteChanged && datesChanged) type = 'move-site-and-dates';
    else if (siteChanged) type = 'move-site';
    else type = 'move-dates';

    const action: PendingQuickAction = {
      type,
      reservation,
      targetSiteAlias: targetSite.alias,
      targetCheckIn,
      targetDays: reservation.days,
    };

    openPendingAction(action, new DOMRect(visual.ghostX - 1, visual.ghostY - 1, 2, 2));
  }, [dates, sites, onReservationClick, openPendingAction]);

  const onDragMouseMove = useCallback((e: MouseEvent) => {
    const reservation = dragReservation.current;
    if (!reservation) return;
    const dx = e.clientX - resDragStartX.current;
    const dy = e.clientY - resDragStartY.current;
    if (!dragMoved.current) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      dragMoved.current = true;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }
    setDragVisual(computeDragVisual(reservation, e.clientX, e.clientY));
  }, [computeDragVisual]);

  const onDragMouseUp = useCallback(() => {
    finishDrag();
  }, [finishDrag]);

  const onDragTouchMove = useCallback((e: TouchEvent) => {
    const reservation = dragReservation.current;
    if (!reservation) return;
    const touch = e.touches[0];
    const dx = touch.clientX - resDragStartX.current;
    const dy = touch.clientY - resDragStartY.current;
    if (!dragMoved.current) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      dragMoved.current = true;
    }
    e.preventDefault();
    setDragVisual(computeDragVisual(reservation, touch.clientX, touch.clientY));
  }, [computeDragVisual]);

  // Keep the stable handler refs pointed at the latest closures
  useEffect(() => {
    onDragMouseMoveHandler.current = onDragMouseMove;
  }, [onDragMouseMove]);
  useEffect(() => {
    onDragMouseUpHandler.current = onDragMouseUp;
  }, [onDragMouseUp]);
  useEffect(() => {
    onDragTouchMoveHandler.current = onDragTouchMove;
  }, [onDragTouchMove]);

  const beginDrag = useCallback((reservation: Reservation, clientX: number, clientY: number) => {
    dragReservation.current = reservation;
    resDragStartX.current = clientX;
    resDragStartY.current = clientY;
    dragOriginCheckInIdx.current = dates.indexOf(reservation.checkIn);
    dragColWidth.current = dates.length > 14 ? 45 : 70;
    dragMoved.current = false;
  }, [dates]);

  const onReservationMouseDown = useCallback((e: React.MouseEvent, reservation: Reservation) => {
    if (!isSingleSite(reservation)) return;
    if (e.button !== 0) return;
    beginDrag(reservation, e.clientX, e.clientY);
    document.addEventListener('mousemove', onDragMouseMoveHandler.current);
    document.addEventListener('mouseup', onDragMouseUpHandler.current);
  }, [isSingleSite, beginDrag]);

  const onReservationTouchStart = useCallback((e: React.TouchEvent, reservation: Reservation) => {
    if (!isSingleSite(reservation)) return;
    const touch = e.touches[0];
    beginDrag(reservation, touch.clientX, touch.clientY);
    document.addEventListener('touchmove', onDragTouchMoveHandler.current, { passive: false });
    document.addEventListener('touchend', onDragMouseUpHandler.current);
  }, [isSingleSite, beginDrag]);

  // Cleanup any dangling drag listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', onDragMouseMoveHandler.current);
      document.removeEventListener('mouseup', onDragMouseUpHandler.current);
      document.removeEventListener('touchmove', onDragTouchMoveHandler.current);
      document.removeEventListener('touchend', onDragMouseUpHandler.current);
    };
  }, []);

  // Compute schedule event bars that overlap the visible date range
  const eventBars = useMemo(() => {
    if (!scheduleEvents || scheduleEvents.length === 0 || dates.length === 0) return [];
    const firstDate = dates[0];
    const lastDate = dates[dates.length - 1];

    return scheduleEvents
      .filter(ev => ev.active && ev.start_date <= lastDate && ev.end_date >= firstDate)
      .map(ev => {
        const startIdx = Math.max(0, dates.indexOf(ev.start_date) === -1
          ? (ev.start_date < firstDate ? 0 : dates.length)
          : dates.indexOf(ev.start_date));
        const endIdx = dates.indexOf(ev.end_date) === -1
          ? (ev.end_date > lastDate ? dates.length - 1 : -1)
          : dates.indexOf(ev.end_date);
        if (endIdx < startIdx) return null;
        return {
          ...ev,
          startCol: startIdx + 2, // +2 because col 1 is the site column (1-indexed)
          span: endIdx - startIdx + 1,
        };
      })
      .filter(Boolean) as (ScheduleEvent & { startCol: number; span: number })[];
  }, [scheduleEvents, dates]);

  // Get the span (number of visible days) for a reservation starting on a given date
  const getReservationSpan = (reservation: Reservation, startDate: string): number => {
    const endIndex = dates.indexOf(reservation.checkOut);
    const startIndex = dates.indexOf(startDate);

    if (endIndex === -1) {
      // Reservation extends beyond visible range
      return dates.length - startIndex;
    }

    return endIndex - startIndex;
  };

  // Check if this is the first visible day of the reservation
  const isReservationStart = (reservation: Reservation, date: string): boolean => {
    const resStart = reservation.checkIn;
    if (resStart === date) return true;
    // If reservation started before visible range, first visible date is the start
    return resStart < dates[0] && date === dates[0];
  };

  const isCompact = dates.length > 14;

  // Helper to determine reservation bar color (priority: safety > visit state > payment > default)
  const getReservationColor = useCallback((reservation: Reservation): string => {
    // 1. Review-based safety warnings (highest priority)
    const review = reservationReviews?.get(reservation.id);
    if (review) {
      if (!review.wouldWelcomeBack) return '#dc2626'; // red  — do not welcome back
      if (review.rating === 1)      return '#92400e'; // brown — 1-star restricted
    }

    // 2. Modified reservation
    if (
      reservation.isCurrentVersion === true &&
      reservation.originalReservationId != null &&
      reservation.supersedesReservationId != null
    ) {
      return '#a927f5'; // purple
    }

    // 3. Visit state from special_requests
    const specialData = parseSpecialRequests(reservation.specialRequests);
    if (specialData.check_outs && specialData.check_outs.length > 0) {
      return '#6b7280'; // gray — checked out
    }
    if (specialData.check_ins && specialData.check_ins.length > 0) {
      return '#3b82f6'; // blue — checked in
    }

    // 4. Pending cash/check payment (payment_type written to special_requests at creation)
    const paymentType = specialData.payment_type?.toLowerCase();
    if (
      (paymentType === 'cash' || paymentType === 'check') &&
      !specialData.check_ins?.length  // not yet checked in = not yet paid
    ) {
      return '#d97706'; // amber — pay cash/check at check-in
    }

    // 5. Default status color
    return getStatusColor(reservation.status);
  }, [reservationReviews]);

  // Compute month spans for the month header row
  const monthSpans = useMemo(() => {
    const spans: { month: string; span: number }[] = [];
    let currentMonth = '';
    for (const date of dates) {
      const month = getMonthName(date);
      if (month === currentMonth) {
        spans[spans.length - 1].span++;
      } else {
        spans.push({ month, span: 1 });
        currentMonth = month;
      }
    }
    return spans;
  }, [dates]);

  // Refs for synced top scrollbar
  const topScrollRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const isSyncing = useRef(false);

  // Sync scroll between top scrollbar and grid container
  useEffect(() => {
    const container = gridContainerRef.current;
    const topScroll = topScrollRef.current;
    if (!container || !topScroll) return;

    const onContainerScroll = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      topScroll.scrollLeft = container.scrollLeft;
      isSyncing.current = false;

      // Notify parent of scroll position for viewport-based loading
      if (onScrollProp) {
        onScrollProp(container.scrollLeft, container.scrollTop);
      }
    };

    const onTopScroll = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      container.scrollLeft = topScroll.scrollLeft;
      isSyncing.current = false;

      // Notify parent of scroll position for viewport-based loading
      if (onScrollProp) {
        onScrollProp(topScroll.scrollLeft, container.scrollTop);
      }
    };

    container.addEventListener('scroll', onContainerScroll);
    topScroll.addEventListener('scroll', onTopScroll);

    return () => {
      container.removeEventListener('scroll', onContainerScroll);
      topScroll.removeEventListener('scroll', onTopScroll);
    };
  }, [onScrollProp]);

  // Keep top scrollbar inner width synced with grid scroll width via ResizeObserver
  const [scrollWidth, setScrollWidth] = useState(0);

  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      setScrollWidth(container.scrollWidth);
    });
    observer.observe(container);

    // Also set initial width
    setScrollWidth(container.scrollWidth);

    return () => observer.disconnect();
  }, [dates.length, siteColumnWidth]);

  // Scroll to today's column automatically when the grid first opens
  const hasScrolledToToday = useRef(false);
  useEffect(() => {
    if (hasScrolledToToday.current) return;
    if (highlightReservationId) return; // let the highlight effect below take over instead
    if (!dates.some(isToday)) return;
    hasScrolledToToday.current = true;
    const timer = setTimeout(() => {
      const el = gridContainerRef.current?.querySelector('.grid-cell--today');
      el?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
    }, 100);
    return () => clearTimeout(timer);
  }, [dates, highlightReservationId]);

  // Scroll to highlighted reservation
  useEffect(() => {
    if (!highlightReservationId) return;
    // Small delay to ensure DOM is rendered
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-reservation-id="${highlightReservationId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        el.classList.add('grid-cell--highlighted');
        const removeTimer = setTimeout(() => el.classList.remove('grid-cell--highlighted'), 3000);
        return () => clearTimeout(removeTimer);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [highlightReservationId]);

  return (
    <div className="schedule-grid-wrapper">
      {/* Top scrollbar synced with the grid container */}
      <div className="top-scrollbar" ref={topScrollRef}>
        <div className="top-scrollbar-inner" style={{ width: scrollWidth || '100%' }} />
      </div>

      <div className="schedule-grid-container" ref={gridContainerRef}>
        <div className={`schedule-grid ${isCompact ? 'schedule-grid--compact' : ''}`}>
          {/* Month header row */}
          <div className="grid-month-header" style={{ gridTemplateColumns: gridColumns }}>
            <div className="grid-cell grid-cell--month-corner" />
            {monthSpans.map((ms, idx) => (
              <div
                key={`${ms.month}-${idx}`}
                className="grid-cell grid-cell--month-header"
                style={{ gridColumn: `span ${ms.span}` }}
              >
                {ms.month}
              </div>
            ))}
          </div>

          {/* Day-of-week / date-number header row */}
          <div className="grid-header" style={{ gridTemplateColumns: gridColumns }}>
            <div className="grid-cell grid-cell--site-header">
              Site
              <div className="column-resize-handle" onMouseDown={onResizeStart} onTouchStart={onResizeTouchStart} />
            </div>
            {dates.map(date => (
              <div
                key={date}
                className={`grid-cell grid-cell--date-header ${isToday(date) ? 'grid-cell--today' : ''}`}
              >
                <span className="date-day">{getDayOfWeek(date + 'T12:00:00')}</span>
                <span className="date-number">{getDayNumber(date + 'T12:00:00')}</span>
              </div>
            ))}
          </div>

          {/* Schedule events banner row */}
          {eventBars.length > 0 && (
            <div className="grid-events-header" style={{ gridTemplateColumns: gridColumns }}>
              <div className="grid-cell grid-cell--events-corner">Events</div>
              {/* Render event bars positioned via grid-column */}
              {eventBars.map(ev => (
                <div
                  key={ev.id}
                  className="schedule-event-bar"
                  style={{
                    gridColumn: `${ev.startCol} / span ${ev.span}`,
                    backgroundColor: ev.color,
                  }}
                  title={`${ev.name}: ${ev.start_date} – ${ev.end_date}`}
                >
                  {ev.name}
                </div>
              ))}
            </div>
          )}

          {/* Site rows grouped by section */}
        {sections.map(section => {
          const sectionSites = sitesBySection[section.id] || [];
          if (sectionSites.length === 0) return null;

          return (
            <React.Fragment key={section.id}>
              {/* Section header */}
              <div className="grid-section-header" style={{ gridTemplateColumns: gridColumns }}>
                <div
                  className="grid-cell grid-cell--section"
                  style={{ gridColumn: `1 / span ${dates.length + 1}` }}
                  onClick={() => toggleSection(section.id)}
                >
                  <span className="section-toggle">{collapsedSections.has(section.id) ? '+' : '−'}</span>
                  {section.name} ({sectionSites.length} sites)
                </div>
              </div>

              {/* Sites in this section */}
              {!collapsedSections.has(section.id) && sectionSites.map(site => {
                const isDropTarget = dragVisual && dragVisual.targetSiteId === site.id;
                const dropTargetClass = isDropTarget
                  ? (dragVisual!.valid ? 'grid-row--drop-target-valid' : 'grid-row--drop-target-invalid')
                  : '';
                return (
                <div
                  key={site.id}
                  data-site-id={site.id}
                  className={`grid-row ${site.seasonal ? 'grid-row--seasonal' : ''} ${dropTargetClass}`}
                  style={{ gridTemplateColumns: gridColumns }}
                >
                  <div className="grid-cell grid-cell--site">
                    <span className="site-alias">{site.alias}</span>
                    <span className="site-type">{site.siteType}</span>
                  </div>

                  {dates.map((date, dateIndex) => {
                    const reservation = reservationMap[site.alias]?.[date];
                    const isStart = reservation && isReservationStart(reservation, date);

                    // If there's a reservation but it's not the start, render empty (handled by colspan)
                    if (reservation && !isStart) {
                      return null;
                    }

                    if (reservation && isStart) {
                      const span = getReservationSpan(reservation, date);
                      const specialData = parseSpecialRequests(reservation.specialRequests);
                      const draggable = isSingleSite(reservation);
                      const isBeingDragged = dragVisual?.reservation.id === reservation.id;
                      return (
                        <div
                          key={date}
                          data-reservation-id={reservation.id}
                          className={`grid-cell grid-cell--reservation ${draggable ? 'grid-cell--reservation-draggable' : ''} ${isBeingDragged ? 'grid-cell--reservation-dragging' : ''}`}
                          style={{
                            gridColumn: `span ${span}`,
                            backgroundColor: getReservationColor(reservation)
                          }}
                          onMouseDown={draggable ? (e) => onReservationMouseDown(e, reservation) : undefined}
                          onTouchStart={draggable ? (e) => onReservationTouchStart(e, reservation) : undefined}
                          onClick={draggable ? undefined : () => onReservationClick(reservation)}
                          data-tooltip={`${reservation.guestName} — ${reservation.confirmationCode}`}
                        >
                          <span className="reservation-guest">{reservation.guestName}</span>
                          {!isCompact && <span className="reservation-code">{reservation.confirmationCode}</span>}
                          {/* Show star badge if flagged */}
                          {specialData.flagged && (
                            <span className="reservation-flag-badge" title="Flagged - see notes">★</span>
                          )}
                        </div>
                      );
                    }

                    // Check for blackout on this site/date
                    const blackout = blackoutMap[site.id]?.[date];
                    if (blackout) {
                      // The hover tooltip and the popover would otherwise show at
                      // once — the cell stays hovered under the transparent backdrop.
                      const popoverOpen = blackoutPopover?.blackoutId === blackout.id;
                      return (
                        <div
                          key={date}
                          data-date={date}
                          className={`grid-cell grid-cell--blackout ${blackoutsEditable ? 'grid-cell--blackout-clickable' : ''} ${isToday(date) ? 'grid-cell--today' : ''}`}
                          onClick={blackoutsEditable ? (e) => handleBlackoutCellClick(e, blackout) : undefined}
                          data-tooltip={popoverOpen
                            ? undefined
                            : `${blackout.title}: ${blackout.start_date} – ${blackout.end_date}${blackoutsEditable ? ' — click to edit' : ''}`}
                        >
                          <span className="blackout-label">{blackout.blackout_type}</span>
                        </div>
                      );
                    }

                    // Empty cell: either a click-to-extend target (adjacent to a single-site reservation)
                    // or a click-to-create target (opens the create-reservation wizard pre-filled)
                    const extendCandidates = adjacentExtendableMap[site.id]?.[date];
                    if (extendCandidates && extendCandidates.length > 0) {
                      const isAmbiguous = extendCandidates.length > 1;
                      const single = extendCandidates[0];
                      const title = isAmbiguous
                        ? `${extendCandidates.length} reservations could extend into this night — click to choose`
                        : `Extend ${single.reservation.guestName}'s stay by ${single.nights} night${single.nights > 1 ? 's' : ''}`;
                      return (
                        <div
                          key={date}
                          data-date={date}
                          className={`grid-cell grid-cell--empty grid-cell--extendable ${isAmbiguous ? 'grid-cell--extendable-multi' : ''} ${isToday(date) ? 'grid-cell--today' : ''}`}
                          onClick={(e) => handleExtendCellClick(e, extendCandidates, date)}
                          title={title}
                        >
                          <span className="extend-affordance">{isAmbiguous ? '?' : '+'}</span>
                        </div>
                      );
                    }

                    if (onEmptyCellClick) {
                      return (
                        <div
                          key={date}
                          data-date={date}
                          className={`grid-cell grid-cell--empty grid-cell--creatable ${isToday(date) ? 'grid-cell--today' : ''}`}
                          onClick={() => onEmptyCellClick(site, date)}
                          title="Create a new reservation"
                        >
                          <span className="create-affordance">+</span>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={date}
                        data-date={date}
                        className={`grid-cell grid-cell--empty ${isToday(date) ? 'grid-cell--today' : ''}`}
                      />
                    );
                  })}
                </div>
              );})}
            </React.Fragment>
          );
        })}
        </div>
      </div>

      {/* Floating drag ghost, follows the pointer while dragging a reservation */}
      {dragVisual && (
        <div
          className={`reservation-drag-ghost ${dragVisual.valid ? 'reservation-drag-ghost--valid' : 'reservation-drag-ghost--invalid'}`}
          style={{
            left: dragVisual.ghostX,
            top: dragVisual.ghostY,
            backgroundColor: getReservationColor(dragVisual.reservation),
          }}
        >
          {dragVisual.reservation.guestName}
        </div>
      )}

      {/* Confirm popover for click-to-extend / drag-and-drop quick actions */}
      {pendingAction && pendingAnchor && onLoadQuickActionPreview && onExecuteQuickAction && (
        <ReservationQuickActionPopover
          action={pendingAction}
          anchorRect={pendingAnchor}
          onLoadPreview={onLoadQuickActionPreview}
          onConfirm={async (action, reason, payment) => {
            await onExecuteQuickAction(action, reason, payment);
            closePendingAction();
          }}
          onCancel={closePendingAction}
        />
      )}

      {/* Chooser shown when a cell's extend zone overlaps more than one reservation */}
      {pendingChoice && (
        <ReservationExtendChoicePopover
          candidates={pendingChoice.candidates}
          anchorRect={pendingChoice.anchor}
          onChoose={handleChooseCandidate}
          onCancel={closePendingChoice}
        />
      )}

      {/* Details / inline editor for a blackout cell */}
      {blackoutPopover && openBlackout && onUpdateBlackout && onDeleteBlackout && (
        <BlackoutDetailPopover
          blackout={openBlackout}
          siteAlias={sites.find(s => s.id === openBlackout.site_id)?.alias}
          anchorRect={blackoutPopover.anchor}
          onSave={onUpdateBlackout}
          onDelete={onDeleteBlackout}
          onClose={closeBlackoutPopover}
        />
      )}
    </div>
  );
};

export default ScheduleGrid;
